import React, { useState, useEffect } from 'react';
import { X, History, User, Truck, Calendar } from 'lucide-react';
import { componentService } from '../../services/componentService';
import { useDateFormat } from '../../hooks/useDateFormat';

const ComponentHistoryModal = ({ componentId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatDate } = useDateFormat();

  useEffect(() => {
    loadHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [componentId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await componentService.getComponentHistory(componentId);
      setHistory(data || []);
    } catch (error) {
      console.error('Error loading history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <History className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Replacement History</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No replacement history yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((record, index) => (
                <div
                  key={record.id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {formatDate(record.replacedDate)}
                      </span>
                      {index === 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          Latest
                        </span>
                      )}
                    </div>
                    {record.workOrderNumber && (
                      <span className="text-sm text-blue-600">
                        WO: {record.workOrderNumber}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Old Part #:</span>
                      <span className="ml-2 text-gray-900">{record.oldPartNumber || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">New Part #:</span>
                      <span className="ml-2 text-gray-900">{record.newPartNumber || '-'}</span>
                    </div>
                    {record.newSerialNumber && (
                      <div>
                        <span className="text-gray-500">New Serial #:</span>
                        <span className="ml-2 text-gray-900">{record.newSerialNumber}</span>
                      </div>
                    )}
                    {record.cost && (
                      <div>
                        <span className="text-gray-500">Cost:</span>
                        <span className="ml-2 text-gray-900">{formatCurrency(record.cost)}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      {record.replacementType === 'INTERNAL' ? (
                        <>
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">
                            {record.replacedByUserName || 'Internal'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Truck className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">
                            {record.replacedBySupplierName || 'External'}
                          </span>
                        </>
                      )}
                    </div>
                    {record.supplierName && (
                      <div className="text-gray-500">
                        Supplier: {record.supplierName}
                      </div>
                    )}
                  </div>

                  {record.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600">{record.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComponentHistoryModal;
